import { useState, useRef, useEffect } from "react";
import { formatPrice } from "../../utils/formatPrice";
import Slider from "react-slick";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment";

const CustomPrevArrow = (props: any) => {
  const { onClick } = props;
  return (
    <ArrowBackIosIcon
      onClick={onClick}
      sx={{
        position: "absolute",
        left: "-18px", // moved more to the left
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        cursor: "pointer",
        color: "black",
        borderRadius: "50%",
        fontSize: "1.3rem",
        "&:hover": { color: "#d3d3d3" },
      }}
    />
  );
};

const CustomNextArrow = (props: any) => {
  const { onClick } = props;
  return (
    <ArrowForwardIosIcon
      onClick={onClick}
      sx={{
        position: "absolute",
        right: "-18px", // moved more to the right
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        cursor: "pointer",
        color: "black",
        borderRadius: "50%",
        fontSize: "1.3rem",
        "&:hover": { color: "#d3d3d3" },
      }}
    />
  );
};

const AvailabilityWidget = ({
  data,
  setFormData,
  formData,
  setPrice,
  setStartPrice,
  isLoading,
}: any) => {
  const [activeSlide, setActiveSlide] = useState<number | null>(null);
  const sliderRef = useRef<any>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Determine number of skeleton cards based on screen size
  const getSkeletonCount = () => {
    if (isMobile) return 2; // Mobile: 2 cards
    if (isTablet) return 3; // Tablet: 3 cards
    return 5; // Desktop: 5 cards
  };
  const handleClick = (index: number, item: any) => {
    setActiveSlide(index);
    const [checkInRaw, checkOutRaw] = item.date_range.split(" - ");
    const checkIn = moment(checkInRaw, "DD/MM/YYYY").format("YYYY-MM-DD");
    const checkOut = moment(checkOutRaw, "DD/MM/YYYY").format("YYYY-MM-DD");

    setFormData((prevFormData: any) => ({
      ...prevFormData,
      checkIn: checkIn,
      checkOut: checkOut,
    }));
    setPrice(item.price || null);
    setStartPrice(item.startPrice || null);
  };

  useEffect(() => {
    if (!data?.length || !formData?.checkIn) return;
    const targetDate = moment(formData.checkIn, "YYYY-MM-DD");
    const foundIndex = data.findIndex((item: any) => {
      const [start] = item.date_range.split(" - ");
      return targetDate.isSame(moment(start, "DD/MM/YYYY"), "day");
    });
    if (foundIndex !== -1) {
      setActiveSlide(foundIndex);
      sliderRef.current?.slickGoTo(foundIndex);
      setPrice(data[foundIndex].price || null);
      setStartPrice(data[foundIndex].startPrice || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, formData.checkIn]);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    initialSlide: 0,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 3 } },
      { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 2, slidesToScroll: 2 } },
    ],
  };

  const formatted = (date_range: string) => {
    let date = date_range
      .split(" - ")
      .map((date) => {
        const [day, month, year] = date.split("/");
        return `${day}/${month}/${year.slice(2)}`;
      })
      .join(" - ");
    return date;
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        ".slick-list": { overflow: "hidden" },
        ".slick-track": { display: "flex" },
        ".slick-arrow": { display: "block !important" },
      }}
    >
      {isLoading ? (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(2, 1fr)", // Mobile: 2 cards
            sm: "repeat(3, 1fr)", // Tablet: 3 cards
            md: "repeat(5, 1fr)", // Desktop: 5 cards
          }}
          gap={1}
          py={2}
        >
          {/* Responsive: Mobile 2 cards, Tablet 3 cards, Desktop 5 cards - all fit available space */}
          {Array.from({ length: getSkeletonCount() }).map((_, index) => (
            <Box
              key={index}
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={2}
              px={1}
              borderLeft="1px solid #d3d3d3"
              borderRight="1px solid #d3d3d3"
              textAlign="center"
              sx={{
                width: "100%",
              }}
            >
              <Skeleton
                variant="text"
                width={80}
                height={20}
                sx={{ mb: 1 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={60}
                height={20}
                sx={{ mb: 1 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={50}
                height={20}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Slider ref={sliderRef} {...settings}>
          {data.map((item: any, index: number) => {
            const isAvailable = item.status === "Available" && item.price >= 1;
            const isOption = item.status === "Optioned";
            const isClickable = isAvailable || isOption;

            const statusColor = isAvailable ? "green" : isOption ? "#f57c00" : "red";
            const statusLabel = isAvailable ? "Available" : isOption ? "On Option" : "Reserved";

            return (
              <Box
                key={index}
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={2}
                borderLeft="1px solid #d3d3d3"
                borderRight="1px solid #d3d3d3"
                textAlign="center"
                sx={{
                  cursor: isClickable ? "pointer" : "not-allowed",
                  background: activeSlide === index ? "#f1f1f1" : "transparent",
                  transition: "background 0.3s ease-in-out",
                  "&:hover": {
                    background: isClickable ? "#f9f9f9" : "transparent",
                  },
                }}
                onClick={() => {
                  if (isClickable) handleClick(index, item);
                }}
              >
                <Typography fontSize="15px" mb={1}>
                  {formatted(item?.date_range)}
                </Typography>

                <Typography fontSize="15px" color={statusColor}>
                  {statusLabel}
                </Typography>

                {isClickable && item?.price >= 1 && (
                  <Typography fontSize="15px" color={statusColor}>
                    {formatPrice(item?.price)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Slider>
      )}
    </Box>
  );
};

export default AvailabilityWidget;
